import { lazy } from "react";
import { Link, Outlet, Route, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import MainLayout from "@/components/MainLayout/MainLayout";
import { AuthenticationGuard } from "@/components/AuthenticationGuard/AuthenticationGuard";
import PermissionGuard from "@/components/PermissionGuard/PermissionGuard";

// Lazy-loaded route components
const CoachingDashboard = lazy(
  () =>
    import(
      "@/modules/coaching/features/Dashboard/components/CoachingDashboard"
    ),
);

const LearningPathsDashboard = lazy(
  () =>
    import(
      "@/modules/coaching/features/LearningPathsDashboard/components/LearningPathsDashboard/LearningPathsDashboard"
    ),
);

const ReviewQueuePage = lazy(
  () =>
    import(
      "@/modules/coaching/features/ReviewQueue/components/ReviewQueuePage"
    ),
);

const PathEngagementDashboard = lazy(
  () =>
    import(
      "@/modules/coaching/features/LearningPathsDashboard/components/PathEngagementDashboard/PathEngagementDashboard"
    ),
);

const BoardPage = lazy(
  () => import("@/modules/coaching/features/Board/components/BoardPage"),
);

const LearnerPage = lazy(
  () => import("@/modules/coaching/features/Learner/components/LearnerPage"),
);

const LearningPathDetail = lazy(
  () =>
    import(
      "@/modules/learningCenter/features/LearningPaths/components/LearningPathDetail/LearningPathDetail"
    ),
);

/**
 * The coaching section's own nav lives in the sidebar, which phones hide in
 * a sheet, so every coaching page carries a way back to the coach's home.
 * The learner page has its own breadcrumb and skips it.
 */
const CoachingBackBar = () => {
  const { pathname } = useLocation();
  if (
    /^\/coaching\/(board\/\d+|learning-paths\/\d+\/engagement)/.test(pathname)
  )
    return null;
  return (
    <div className="mx-auto max-w-7xl px-4 pt-3 sm:px-6 lg:px-8">
      <Link
        className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 text-[13px] font-semibold text-gray-700 hover:bg-gray-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        data-testid="coaching-back"
        to="/hub/learning-center/coaches-learning"
      >
        <ArrowLeft className="h-4 w-4" />
        Coaches Learning
      </Link>
    </div>
  );
};

/** Coaching pages are learning-path pages: brand typeface (Manrope). */
const BrandFontOutlet = () => (
  <div className="contents font-brand">
    <CoachingBackBar />
    <Outlet />
  </div>
);

// Create stable component reference
const CoachingMainLayout = () => <MainLayout section="coaching" />;

const CoachingRoutes = (
  <Route
    element={<AuthenticationGuard component={CoachingMainLayout} />}
    path="/coaching"
  >
    <Route element={<BrandFontOutlet />}>
      <Route element={<CoachingDashboard />} path="dashboard" />
      {/* Member board: every enrollment the coach owns, by status (Approach A
        of docs/learning-center/coach-experience-review.md). */}
      <Route
        element={
          <PermissionGuard requiredRoles={["ROLE_COACH", "ROLE_SUPER_ADMIN"]}>
            <BoardPage />
          </PermissionGuard>
        }
        path="board"
      />
      {/* One learner on one path: the action-first page behind a board card. */}
      <Route
        element={
          <PermissionGuard requiredRoles={["ROLE_COACH", "ROLE_SUPER_ADMIN"]}>
            <LearnerPage />
          </PermissionGuard>
        }
        path="board/:enrollmentId"
      />
      <Route
        element={
          <PermissionGuard requiredRoles={["ROLE_COACH", "ROLE_SUPER_ADMIN"]}>
            <LearningPathsDashboard />
          </PermissionGuard>
        }
        path="learning-paths"
      />
      {/* No role guard: a step's named owner need not be a coach; the API
        scopes the queue to the caller. */}
      <Route element={<ReviewQueuePage />} path="reviews" />
      {/* Param is :id (not :pathId) because LearningPathDetail reads
        useParams<{ id }> — it is shared with /hub/learning-center/paths/:id. */}
      <Route
        element={
          <PermissionGuard requiredRoles={["ROLE_COACH", "ROLE_SUPER_ADMIN"]}>
            <LearningPathDetail />
          </PermissionGuard>
        }
        path="learning-paths/:id"
      />
      <Route
        element={
          <PermissionGuard requiredRoles={["ROLE_COACH", "ROLE_SUPER_ADMIN"]}>
            <PathEngagementDashboard />
          </PermissionGuard>
        }
        path="learning-paths/:pathId/engagement"
      />
    </Route>
  </Route>
);

export default CoachingRoutes;
